import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiGoogledrive, SiDropbox } from 'react-icons/si';
import { FaMicrosoft, FaYandex } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SketchButton } from './sketchy';
import type { StorageProvider } from '../services/storageService';
import ga from '../services/analytics';

const PROVIDER_CARDS: { id: StorageProvider; icon: React.ReactNode }[] = [
  { id: 'gdrive', icon: <SiGoogledrive className="w-6 h-6" /> },
  { id: 'dropbox', icon: <SiDropbox className="w-6 h-6" /> },
  { id: 'onedrive', icon: <FaMicrosoft className="w-6 h-6" /> },
  { id: 'yandex', icon: <FaYandex className="w-6 h-6" /> },
];

interface PostMergeCloudPickerProps {
  isOpen: boolean;
  eligibleForCassetteProjectStorage?: boolean;
  onChooseCassetteCloud: () => Promise<void>;
  onChooseExternal: (provider: StorageProvider) => Promise<void>;
  onCassetteCloudUpsell?: () => void;
  onDismiss?: () => Promise<void>;
}

const PostMergeCloudPicker: React.FC<PostMergeCloudPickerProps> = ({
  isOpen,
  eligibleForCassetteProjectStorage = true,
  onChooseCassetteCloud,
  onChooseExternal,
  onCassetteCloudUpsell,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<StorageProvider | 'cassette' | 'dismiss' | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onDismiss && busy === null) {
        void handleDismiss();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onDismiss, busy]);

  if (!isOpen) return null;

  const trackChoice = (provider: string) => {
    ga.event('cloud_onboarding_choice', { provider, context: 'post_anon_merge' });
  };

  const handleCassette = async () => {
    setBusy('cassette');
    try {
      trackChoice('cassette_s3');
      await onChooseCassetteCloud();
    } finally {
      setBusy(null);
    }
  };

  const handleExternal = async (provider: StorageProvider) => {
    setBusy(provider);
    try {
      trackChoice(provider);
      await onChooseExternal(provider);
    } finally {
      setBusy(null);
    }
  };

  const handleDismiss = async () => {
    if (!onDismiss) return;
    setBusy('dismiss');
    try {
      ga.event('cloud_onboarding_dismissed', { context: 'post_anon_merge' });
      await onDismiss();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" data-testid="post-merge-cloud-picker">
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto relative" role="dialog">
        {onDismiss && (
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={busy !== null}
            className="absolute top-4 right-4 p-1 rounded-lg text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-hover)] disabled:opacity-50"
            aria-label={t('auth.cloudPicker.dismiss', 'Use Cassette Cloud for now')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
        <h2 className="text-xl font-bold text-[var(--color-text)] mb-2 pr-8">
          {t('auth.cloudPicker.title', 'Choose where to store your music')}
        </h2>
        <p className="text-[var(--color-muted)] text-sm mb-4">
          {t('auth.cloudPicker.subtitle', 'Pick a cloud provider for your tracks.')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {PROVIDER_CARDS.map(({ id, icon }) => (
            <SketchButton
              key={id}
              onClick={() => handleExternal(id)}
              disabled={busy !== null}
              className="w-full flex items-center justify-center gap-2"
            >
              {icon}
              {t(`storage.providers.${id}`, id)}
            </SketchButton>
          ))}
        </div>
        {eligibleForCassetteProjectStorage && (
        <SketchButton
          onClick={handleCassette}
          disabled={busy !== null}
          className="w-full"
          data-testid="post-merge-cassette-cloud"
        >
          {t('storage.continueWithCassetteCloud', 'Use Cassette Cloud as project storage')}
        </SketchButton>
        )}
        {!eligibleForCassetteProjectStorage && (
          <SketchButton
            onClick={() => onCassetteCloudUpsell?.()}
            disabled={busy !== null || !onCassetteCloudUpsell}
            className="w-full"
            data-testid="post-merge-cassette-upgrade"
          >
            {t('settings.storage.cassetteCloudPaywallTitle', 'Cassette Cloud requires a cloud plan')}
          </SketchButton>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={busy !== null}
            className="w-full mt-3 py-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            {t('auth.cloudPicker.dismiss', 'Use Cassette Cloud for now')}
          </button>
        )}
        <p className="text-xs text-[var(--color-muted)] mt-2 text-center">
          {t(
            'auth.cloudPicker.cassetteFreeNote',
            'Free plan includes up to 3 tracks you can create and edit.',
          )}
        </p>
        <p className="text-xs text-[var(--color-muted)] mt-2 text-center">
          {t(
            'storage.collaborationHint',
            'If you collaborate, use the same cloud provider as your team. File sharing pauses until providers match.',
          )}
        </p>
      </div>
    </div>
  );
};

export default PostMergeCloudPicker;
